import * as i3 from '@angular/cdk/table';
import { CdkCellDef, CdkHeaderCellDef, CdkFooterCellDef, CdkColumnDef, CdkHeaderCell, CdkFooterCell, CdkCell, CdkHeaderRowDef, CdkFooterRowDef, CdkRowDef, CdkHeaderRow, CdkCellOutlet, CdkFooterRow, CdkRow, CdkTable, _COALESCED_STYLE_SCHEDULER, STICKY_POSITIONING_LISTENER, CDK_TABLE, _CoalescedStyleScheduler, HeaderRowOutlet, DataRowOutlet, NoDataRowOutlet, FooterRowOutlet, CdkTextColumn, CdkTableModule, DataSource } from '@angular/cdk/table';
import * as i0 from '@angular/core';
import { InjectionToken, EventEmitter, booleanAttribute, Directive, Optional, Inject, Input, Output, Component, ViewEncapsulation, ChangeDetectionStrategy, Attribute, SkipSelf, NgModule } from '@angular/core';
import { SbbCommonModule } from '@sbb-esta/angular/core';
import { ReplaySubject, Subject, merge, fromEvent, BehaviorSubject, of, combineLatest } from 'rxjs';
import * as i2 from '@angular/cdk/a11y';
import { SPACE, ENTER } from '@angular/cdk/keycodes';
import { trigger, state, style, transition, animate, keyframes, query, animateChild } from '@angular/animations';
import { coerceBooleanProperty, _isNumberValue } from '@angular/cdk/coercion';
import * as i1 from '@angular/cdk/bidi';
import { _VIEW_REPEATER_STRATEGY, _RecycleViewRepeaterStrategy, _DisposeViewRepeaterStrategy } from '@angular/cdk/collections';
import * as i2$1 from '@angular/cdk/platform';
import { normalizePassiveListenerOptions } from '@angular/cdk/platform';
import * as i3$1 from '@angular/cdk/scrolling';
import { DOCUMENT } from '@angular/common';
import { takeUntil, startWith, map, distinctUntilChanged } from 'rxjs/operators';

/** @docs-private */
function getSortDuplicateSortableIdError(id) {
    return Error(`Cannot have two SbbSortables with the same id (${id}).`);
}
/** @docs-private */
function getSortHeaderNotContainedWithinSortError() {
    return Error(`SbbSortHeader must be placed within a parent element with the SbbSort directive.`);
}
/** @docs-private */
function getSortHeaderMissingIdError() {
    return Error(`SbbSortHeader must be provided with a unique id.`);
}
/** @docs-private */
function getSortInvalidDirectionError(direction) {
    return Error(`${direction} is not a valid sort direction ('asc' or 'desc').`);
}

/** Injection token to be used to override the default options for `sbb-sort`. */
const SBB_SORT_DEFAULT_OPTIONS = new InjectionToken('SBB_SORT_DEFAULT_OPTIONS');
/** Container for SbbSortables to manage the sort state and provide default sort parameters. */
class SbbSort {
    /** The sort direction of the currently active SbbSortable. */
    get direction() {
        return this._direction;
    }
    set direction(direction) {
        if (direction &&
            direction !== 'asc' &&
            direction !== 'desc' &&
            (typeof ngDevMode === 'undefined' || ngDevMode)) {
            throw getSortInvalidDirectionError(direction);
        }
        this._direction = direction;
    }
    constructor(_defaultOptions) {
        this._defaultOptions = _defaultOptions;
        this._initializedStream = new ReplaySubject(1);
        /** Collection of all registered sortables that this directive manages. */
        this.sortables = new Map();
        /** Used to notify any child components listening to state changes. */
        this._stateChanges = new Subject();
        /** Whether the sort is disabled. */
        this.disabled = false;
        /**
         * The direction to set when an SbbSortable is initially sorted.
         * May be overridden by the SbbSortable's sort start.
         */
        this.start = 'asc';
        this._direction = '';
        /** Event emitted when the user changes either the active sort or sort direction. */
        this.sortChange = new EventEmitter();
        /** Emits when the paginator is initialized. */
        this.initialized = this._initializedStream;
    }
    /**
     * Register function to be used by the contained SbbSortables. Adds the SbbSortable to the
     * collection of SbbSortables.
     */
    register(sortable) {
        if (typeof ngDevMode === 'undefined' || ngDevMode) {
            if (!sortable.id) {
                throw getSortHeaderMissingIdError();
            }
            if (this.sortables.has(sortable.id)) {
                throw getSortDuplicateSortableIdError(sortable.id);
            }
        }
        this.sortables.set(sortable.id, sortable);
    }
    /**
     * Unregister function to be used by the contained SbbSortables. Removes the SbbSortable from the
     * collection of contained SbbSortables.
     */
    deregister(sortable) {
        this.sortables.delete(sortable.id);
    }
    /** Sets the active sort id and determines the new sort direction. */
    sort(sortable) {
        if (this.active !== sortable.id) {
            this.active = sortable.id;
            this.direction = sortable.start ? sortable.start : this.start;
        }
        else {
            this.direction = this.getNextSortDirection(sortable);
        }
        this.sortChange.emit({ active: this.active, direction: this.direction });
    }
    /** Returns the next sort direction of the active sortable, checking for potential overrides. */
    getNextSortDirection(sortable) {
        if (!sortable) {
            return '';
        }
        // Get the sort direction cycle with the potential sortable overrides.
        const disableClear = sortable?.disableClear ?? this.disableClear ?? !!this._defaultOptions?.disableClear;
        const sortDirectionCycle = getSortDirectionCycle(sortable.start || this.start, disableClear);
        // Get and return the next direction in the cycle
        let nextDirectionIndex = sortDirectionCycle.indexOf(this.direction) + 1;
        if (nextDirectionIndex >= sortDirectionCycle.length) {
            nextDirectionIndex = 0;
        }
        return sortDirectionCycle[nextDirectionIndex];
    }
    ngOnInit() {
        this._initializedStream.next();
    }
    ngOnChanges() {
        this._stateChanges.next();
    }
    ngOnDestroy() {
        this._stateChanges.complete();
        this._initializedStream.complete();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbSort, deps: [{ token: SBB_SORT_DEFAULT_OPTIONS, optional: true }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "16.1.0", version: "17.3.7", type: SbbSort, isStandalone: true, selector: "[sbbSort]", inputs: { active: ["sbbSortActive", "active"], disabled: ["sbbSortDisabled", "disabled", booleanAttribute], start: ["sbbSortStart", "start"], direction: ["sbbSortDirection", "direction"], disableClear: ["sbbSortDisableClear", "disableClear", booleanAttribute] }, outputs: { sortChange: "sbbSortChange" }, host: { classAttribute: "sbb-sort" }, exportAs: ["sbbSort"], usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbSort, decorators: [{
            type: Directive,
            args: [{
                    selector: '[sbbSort]',
                    exportAs: 'sbbSort',
                    host: { class: 'sbb-sort' },
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [SBB_SORT_DEFAULT_OPTIONS]
                }] }], propDecorators: { active: [{
                type: Input,
                args: ['sbbSortActive']
            }], disabled: [{
                type: Input,
                args: [{ alias: 'sbbSortDisabled', transform: booleanAttribute }]
            }], start: [{
                type: Input,
                args: ['sbbSortStart']
            }], direction: [{
                type: Input,
                args: ['sbbSortDirection']
            }], disableClear: [{
                type: Input,
                args: [{ alias: 'sbbSortDisableClear', transform: booleanAttribute }]
            }], sortChange: [{
                type: Output,
                args: ['sbbSortChange']
            }] } });
/** Returns the sort direction cycle to use given the provided parameters of order and clear. */
function getSortDirectionCycle(start, disableClear) {
    const sortOrder = ['asc', 'desc'];
    if (start === 'desc') {
        sortOrder.reverse();
    }
    if (!disableClear) {
        sortOrder.push('');
    }
    return sortOrder;
}

const SORT_ANIMATION_TRANSITION = '225ms cubic-bezier(0.4,0.0,0.2,1)';
/**
 * Animations used by SbbSort.
 * @docs-private
 */
const sbbSortAnimations = {
    /** Animation that moves the sort indicator. */
    indicator: trigger('indicator', [
        state('active-asc, asc', style({ transform: 'scaleY(-1)' })),
        state('active-desc, desc', style({ transform: 'scaleY(1)' })),
        transition('active-asc <=> active-desc', animate(SORT_ANIMATION_TRANSITION)),
        transition('asc <=> desc', animate('0ms')),
    ]),
    /** Animation that controls the arrow opacity. */
    arrowOpacity: trigger('arrowOpacity', [
        state('desc-to-active, asc-to-active, active', style({ opacity: 1 })),
        state('desc-to-hint, asc-to-hint, hint', style({ opacity: 0.54 })),
        state('hint-to-desc, active-to-desc, desc, hint-to-asc, active-to-asc, asc, void', style({ opacity: 0 })),
        // Transition between all states except for immediate transitions
        transition('* => asc, * => desc, * => active, * => hint, * => void', animate('0ms')),
        transition('* <=> *', animate(SORT_ANIMATION_TRANSITION)),
    ]),
    /**
     * Animation for the translation of the arrow as a whole. States are separated into two
     * groups: ones with animations and others that are immediate. Immediate states are asc, desc,
     * peek, and active. The other states define a specific animation (source-to-destination)
     * and are determined as a function of their prev user-perceived state and what the next state
     * should be.
     */
    arrowPosition: trigger('arrowPosition', [
        // Hidden Above => Hint Center
        transition('* => desc-to-hint, * => desc-to-active', animate(SORT_ANIMATION_TRANSITION, keyframes([
            style({ transform: 'translateY(-25%)' }),
            style({ transform: 'translateY(0)' }),
        ]))),
        // Hint Center => Hidden Below
        transition('* => hint-to-desc, * => active-to-desc', animate(SORT_ANIMATION_TRANSITION, keyframes([style({ transform: 'translateY(0)' }), style({ transform: 'translateY(25%)' })]))),
        // Hidden Below => Hint Center
        transition('* => asc-to-hint, * => asc-to-active', animate(SORT_ANIMATION_TRANSITION, keyframes([style({ transform: 'translateY(25%)' }), style({ transform: 'translateY(0)' })]))),
        // Hint Center => Hidden Above
        transition('* => hint-to-asc, * => active-to-asc', animate(SORT_ANIMATION_TRANSITION, keyframes([
            style({ transform: 'translateY(0)' }),
            style({ transform: 'translateY(-25%)' }),
        ]))),
        state('desc-to-hint, asc-to-hint, hint, desc-to-active, asc-to-active, active', style({ transform: 'translateY(0)' })),
        state('hint-to-desc, active-to-desc, desc', style({ transform: 'translateY(-25%)' })),
        state('hint-to-asc, active-to-asc, asc', style({ transform: 'translateY(25%)' })),
    ]),
    /** Necessary trigger that calls animate on children animations. */
    allowChildren: trigger('allowChildren', [
        transition('* <=> *', [query('@*', animateChild(), { optional: true })]),
    ]),
};

/**
 * Applies sorting behavior (click to change sort) and styles to an element, including an
 * arrow to display the current sort direction.
 *
 * Must be provided with an id and contained within a parent SbbSort.
 *
 * If used on header cells in a CdkTable, it will automatically default its id from its containing
 * column definition.
 */
class SbbSortHeader {
    /**
     * Description applied to SbbSortHeader's button element with aria-describedby. This text should
     * describe the action that will occur when the user clicks the sort header.
     */
    get sortActionDescription() {
        return this._sortActionDescription;
    }
    set sortActionDescription(value) {
        this._updateSortActionDescription(value);
    }
    constructor(_changeDetectorRef, 
    // `SbbSort` is not optionally injected, but just asserted manually w/ better error.
    _sort, _columnDef, 
    // Also Inject MAT_SORT_HEADER_COLUMN_DEF to provide full cdkTable support
    _columnDefCdk, _focusMonitor, _elementRef, _ariaDescriber, defaultOptions) {
        this._changeDetectorRef = _changeDetectorRef;
        this._sort = _sort;
        this._columnDef = _columnDef;
        this._columnDefCdk = _columnDefCdk;
        this._focusMonitor = _focusMonitor;
        this._elementRef = _elementRef;
        this._ariaDescriber = _ariaDescriber;
        /**
         * Flag set to true when the indicator should be displayed while the sort is not active. Used to
         * provide an affordance that the header is sortable by showing on focus and hover.
         */
        this._showIndicatorHint = false;
        /**
         * The view transition state of the arrow (translation/ opacity) - indicates its `from` and `to`
         * position through the animation. If animations are currently disabled, the fromState is removed
         * so that there is no animation displayed.
         */
        this._viewState = {};
        /** The direction the arrow should be facing according to the current state. */
        this._arrowDirection = '';
        /** Whether the view state animation should show the transition between the `from` and `to` states. */
        this._disableViewStateAnimation = false;
        /** Sets the position of the arrow that displays when sorted. */
        this.arrowPosition = 'after';
        this.disabled = false;
        // Default the action description to "Sort" because it's better than nothing.
        // Without a description, the button's label comes from the sort header text content,
        // which doesn't give any indication that it performs a sorting operation.
        this._sortActionDescription = 'Sort';
        // Note that we use a string token for the `_columnDef`, because the value is provided both by
        // `angular/table` and `cdk/table` and we can't have the CDK depending on SBB Angular,
        // and we want to avoid having the sort header depending on the CDK table because
        // of this single reference.
        if (!_sort && (typeof ngDevMode === 'undefined' || ngDevMode)) {
            throw getSortHeaderNotContainedWithinSortError();
        }
        if (defaultOptions?.arrowPosition) {
            this.arrowPosition = defaultOptions?.arrowPosition;
        }
        this._handleStateChanges();
    }
    ngOnInit() {
        if (!this.id && this._columnDef) {
            this.id = this._columnDef.name;
        }
        else if (!this.id && this._columnDefCdk) {
            this.id = this._columnDefCdk.name;
        }
        // Initialize the direction of the arrow and set the view state to be immediately that state.
        this._updateArrowDirection();
        this._setAnimationTransitionState({
            toState: this._isSorted() ? 'active' : this._arrowDirection,
        });
        this._sort.register(this);
        this._sortButton = this._elementRef.nativeElement.querySelector('.sbb-sort-header-container');
        this._updateSortActionDescription(this._sortActionDescription);
    }
    ngAfterViewInit() {
        // We use the focus monitor because we also want to style
        // things differently based on the focus origin.
        this._focusMonitor.monitor(this._elementRef, true).subscribe((origin) => {
            const newState = !!origin;
            if (newState !== this._showIndicatorHint) {
                this._setIndicatorHintVisible(newState);
                this._changeDetectorRef.markForCheck();
            }
        });
    }
    ngOnDestroy() {
        this._focusMonitor.stopMonitoring(this._elementRef);
        this._sort.deregister(this);
        this._rerenderSubscription.unsubscribe();
    }
    /**
     * Sets the "hint" state such that the arrow will be semi-transparently displayed as a hint to the
     * user showing what the active sort will become. If set to false, the arrow will fade away.
     */
    _setIndicatorHintVisible(visible) {
        // No-op if the sort header is disabled - should not make the hint visible.
        if (this._isDisabled() && visible) {
            return;
        }
        this._showIndicatorHint = visible;
        if (!this._isSorted()) {
            this._updateArrowDirection();
            if (this._showIndicatorHint) {
                this._setAnimationTransitionState({ fromState: this._arrowDirection, toState: 'hint' });
            }
            else {
                this._setAnimationTransitionState({ fromState: 'hint', toState: this._arrowDirection });
            }
        }
    }
    /**
     * Sets the animation transition view state for the arrow's position and opacity. If the
     * `disableViewStateAnimation` flag is set to true, the `fromState` will be ignored so that
     * no animation appears.
     */
    _setAnimationTransitionState(viewState) {
        this._viewState = viewState || {};
        // If the animation for arrow position state (opacity/translation) should be disabled,
        // remove the fromState so that it jumps right to the toState.
        if (this._disableViewStateAnimation) {
            this._viewState = { toState: viewState.toState };
        }
    }
    /** Triggers the sort on this sort header and removes the indicator hint. */
    _toggleOnInteraction() {
        this._sort.sort(this);
        // Do not show the animation if the header was already shown in the right position.
        if (this._viewState.toState === 'hint' || this._viewState.toState === 'active') {
            this._disableViewStateAnimation = true;
        }
    }
    _handleClick() {
        if (!this._isDisabled()) {
            this._sort.sort(this);
        }
    }
    _handleKeydown(event) {
        if (!this._isDisabled() && (event.keyCode === SPACE || event.keyCode === ENTER)) {
            event.preventDefault();
            this._toggleOnInteraction();
        }
    }
    /** Whether this SbbSortHeader is currently sorted in either ascending or descending order. */
    _isSorted() {
        return (this._sort.active === this.id &&
            (this._sort.direction === 'asc' || this._sort.direction === 'desc'));
    }
    /** Returns the animation state for the arrow direction (indicator and pointers). */
    _getArrowDirectionState() {
        return `${this._isSorted() ? 'active-' : ''}${this._arrowDirection}`;
    }
    /** Returns the arrow position state (opacity, translation). */
    _getArrowViewState() {
        const fromState = this._viewState.fromState;
        return (fromState ? `${fromState}-to-` : '') + this._viewState.toState;
    }
    /**
     * Updates the direction the arrow should be pointing. If it is not sorted, the arrow should be
     * facing the start direction. Otherwise if it is sorted, the arrow should point in the currently
     * active sorted direction. The reason this is updated through a function is because the direction
     * should only be changed at specific times - when deactivated but the hint is displayed and when
     * the sort is active and the direction changes. Otherwise the arrow's direction should linger
     * in cases such as the sort becoming deactivated but we want to animate the arrow away while
     * preserving its direction, even though the next sort direction is actually different and should
     * only be changed once the arrow displays again (hint or activation).
     */
    _updateArrowDirection() {
        this._arrowDirection = this._isSorted() ? this._sort.direction : this.start || this._sort.start;
    }
    _isDisabled() {
        return this._sort.disabled || this.disabled;
    }
    /**
     * Gets the aria-sort attribute that should be applied to this sort header. If this header
     * is not sorted, returns null so that the attribute is removed from the host element. Aria spec
     * says that the aria-sort property should only be present on one header at a time, so removing
     * ensures this is true.
     */
    _getAriaSortAttribute() {
        if (!this._isSorted()) {
            return 'none';
        }
        return this._sort.direction === 'asc' ? 'ascending' : 'descending';
    }
    /** Whether the arrow inside the sort header should be rendered. */
    _renderArrow() {
        return !this._isDisabled() || this._isSorted();
    }
    _updateSortActionDescription(newDescription) {
        // We use AriaDescriber for the sort button instead of setting an `aria-label` because some
        // screen readers (notably VoiceOver) will read both the column header *and* the button's label
        // for every *cell* in the table, creating a lot of unnecessary noise.
        // If _sortButton is undefined, the component hasn't been initialized yet so there's
        // nothing to update in the DOM.
        if (this._sortButton) {
            // removeDescription will no-op if there is no existing message.
            this._ariaDescriber.removeDescription(this._sortButton, this._sortActionDescription);
            this._ariaDescriber.describe(this._sortButton, newDescription);
        }
        this._sortActionDescription = newDescription;
    }
    /** Handles changes in the sorting state. */
    _handleStateChanges() {
        this._rerenderSubscription = merge(this._sort.sortChange, this._sort._stateChanges).subscribe(() => {
            if (this._isSorted()) {
                this._updateArrowDirection();
                // Do not show the animation if the header was already shown in the right position.
                if (this._viewState.toState === 'hint' || this._viewState.toState === 'active') {
                    this._disableViewStateAnimation = true;
                }
                this._setAnimationTransitionState({ fromState: this._arrowDirection, toState: 'active' });
                this._showIndicatorHint = false;
            }
            // If this header was recently active and now no longer sorted, animate away the arrow.
            if (!this._isSorted() && this._viewState && this._viewState.toState === 'active') {
                this._disableViewStateAnimation = false;
                this._setAnimationTransitionState({ fromState: 'active', toState: this._arrowDirection });
            }
            this._changeDetectorRef.markForCheck();
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbSortHeader, deps: [{ token: i0.ChangeDetectorRef }, { token: SbbSort, optional: true }, { token: 'SBB_SORT_HEADER_COLUMN_DEF', optional: true }, { token: 'MAT_SORT_HEADER_COLUMN_DEF', optional: true }, { token: i2.FocusMonitor }, { token: i0.ElementRef }, { token: i2.AriaDescriber }, { token: SBB_SORT_DEFAULT_OPTIONS, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "17.3.7", type: SbbSortHeader, isStandalone: true, selector: "[sbb-sort-header]", inputs: { id: ["sbb-sort-header", "id"], arrowPosition: "arrowPosition", disabled: ["disabled", "disabled", booleanAttribute], start: "start", sortActionDescription: "sortActionDescription", disableClear: ["disableClear", "disableClear", booleanAttribute] }, host: { listeners: { "click": "_handleClick()", "keydown": "_handleKeydown($event)", "mouseenter": "_setIndicatorHintVisible(true)", "mouseleave": "_setIndicatorHintVisible(false)" }, properties: { "attr.aria-sort": "_getAriaSortAttribute()", "class.sbb-sort-header-disabled": "_isDisabled()" }, classAttribute: "sbb-sort-header" }, exportAs: ["sbbSortHeader"], ngImport: i0, template: "<!--\nWe set the `tabindex` on an element inside the table header, rather than the header itself,\nbecause of a bug in NVDA where having a `tabindex` on a `th` breaks keyboard navigation in the\ntable (see https://github.com/nvaccess/nvda/issues/7718). This allows for the header to both\nbe focusable, and have screen readers read out its `aria-sort` state. We prefer this approach\nover having a button with an `aria-label` inside the header, because the button's `aria-label`\nwill be read out as the user is navigating the table's cell (see #13012).\n\nThe approach is based off of: https://dequeuniversity.com/library/aria/tables/sf-sortable-grid\n-->\n<div\n  class=\"sbb-sort-header-container\"\n  [class.sbb-sort-header-sorted]=\"_isSorted()\"\n  [class.sbb-sort-header-position-before]=\"arrowPosition === 'before'\"\n  [attr.tabindex]=\"_isDisabled() ? null : 0\"\n  [attr.role]=\"_isDisabled() ? null : 'button'\"\n>\n  <div class=\"sbb-sort-header-content\">\n    <ng-content></ng-content>\n  </div>\n\n  <!-- Disable animations while a current animation is running -->\n  @if (_renderArrow()) {\n    <div\n      class=\"sbb-sort-header-arrow\"\n      [@arrowOpacity]=\"_getArrowViewState()\"\n      [@arrowPosition]=\"_getArrowViewState()\"\n      [@allowChildren]=\"_getArrowDirectionState()\"\n      (@arrowPosition.start)=\"_disableViewStateAnimation = true\"\n      (@arrowPosition.done)=\"_disableViewStateAnimation = false\"\n    >\n      <!-- This is a modified version of the sbb arrow-down-small icon to support the animation -->\n      <svg\n        xmlns=\"http://www.w3.org/2000/svg\"\n        width=\"24\"\n        height=\"24\"\n        viewBox=\"0 0 24 24\"\n        preserveAspectRatio=\"xMidYMid meet\"\n        focusable=\"false\"\n      >\n        <path\n          fill=\"none\"\n          fill-rule=\"evenodd\"\n          stroke=\"currentColor\"\n          stroke-width=\"1\"\n          d=\"M11.5,5.75 L11.5,18.25\"\n        ></path>\n        <path\n          fill=\"none\"\n          fill-rule=\"evenodd\"\n          stroke=\"currentColor\"\n          stroke-width=\"1\"\n          d=\"M7.5,14.25 L11.5,18.25 L15.5,14.25\"\n          style=\"transform-origin: center\"\n          [@indicator]=\"_getArrowDirectionState()\"\n        ></path>\n      </svg>\n    </div>\n  }\n</div>\n", styles: [".sbb-sort-header{cursor:pointer;position:relative}.sbb-sort-header:is(.cdk-keyboard-focused,.cdk-program-focused){outline:var(--sbb-border-width-thin) solid var(--sbb-color-weak-accent);outline-offset:var(--sbb-border-width-thin-negative)}.sbb-sort-header:hover .sbb-sort-header-container{color:var(--sbb-color-call-to-action-hover)}.sbb-sort-header-sorted{color:var(--sbb-color-text)}.sbb-sort-header-container{display:flex;letter-spacing:normal;text-align:left;height:100%;outline:0}.sbb-sort-header-disabled .sbb-sort-header-container{cursor:default}.sbb-table-align-center .sbb-sort-header-container{text-align:center}.sbb-sort-header-content{flex-shrink:1;flex-grow:1}.sbb-sort-header-position-before{flex-direction:row-reverse}.sbb-sort-header-arrow{height:var(--sbb-icon-size-default);width:var(--sbb-icon-size-default);min-width:var(--sbb-icon-size-default);position:relative;opacity:0;margin:calc(-0.0666666667rem*var(--sbb-scaling-factor)) calc(-0.5333333333rem*var(--sbb-scaling-factor)) calc(-0.0666666667rem*var(--sbb-scaling-factor)) var(--sbb-table-sort-header-arrow-margin)}.sbb-sort-header-position-before .sbb-sort-header-arrow{margin:calc(-0.0666666667rem*var(--sbb-scaling-factor)) var(--sbb-table-sort-header-arrow-margin) calc(-0.0666666667rem*var(--sbb-scaling-factor)) calc(-0.0666666667rem*var(--sbb-scaling-factor))}.sbb-sort-header-arrow svg{width:100%;height:100%}"], animations: [
            sbbSortAnimations.arrowOpacity,
            sbbSortAnimations.arrowPosition,
            sbbSortAnimations.allowChildren,
            sbbSortAnimations.indicator,
        ], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbSortHeader, decorators: [{
            type: Component,
            args: [{ selector: '[sbb-sort-header]', exportAs: 'sbbSortHeader', host: {
                        class: 'sbb-sort-header',
                        '(click)': '_handleClick()',
                        '(keydown)': '_handleKeydown($event)',
                        '(mouseenter)': '_setIndicatorHintVisible(true)',
                        '(mouseleave)': '_setIndicatorHintVisible(false)',
                        '[attr.aria-sort]': '_getAriaSortAttribute()',
                        '[class.sbb-sort-header-disabled]': '_isDisabled()',
                    }, encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, animations: [
                        sbbSortAnimations.arrowOpacity,
                        sbbSortAnimations.arrowPosition,
                        sbbSortAnimations.allowChildren,
                        sbbSortAnimations.indicator,
                    ], standalone: true, template: "<!--\nWe set the `tabindex` on an element inside the table header, rather than the header itself,\nbecause of a bug in NVDA where having a `tabindex` on a `th` breaks keyboard navigation in the\ntable (see https://github.com/nvaccess/nvda/issues/7718). This allows for the header to both\nbe focusable, and have screen readers read out its `aria-sort` state. We prefer this approach\nover having a button with an `aria-label` inside the header, because the button's `aria-label`\nwill be read out as the user is navigating the table's cell (see #13012).\n\nThe approach is based off of: https://dequeuniversity.com/library/aria/tables/sf-sortable-grid\n-->\n<div\n  class=\"sbb-sort-header-container\"\n  [class.sbb-sort-header-sorted]=\"_isSorted()\"\n  [class.sbb-sort-header-position-before]=\"arrowPosition === 'before'\"\n  [attr.tabindex]=\"_isDisabled() ? null : 0\"\n  [attr.role]=\"_isDisabled() ? null : 'button'\"\n>\n  <div class=\"sbb-sort-header-content\">\n    <ng-content></ng-content>\n  </div>\n\n  <!-- Disable animations while a current animation is running -->\n  @if (_renderArrow()) {\n    <div\n      class=\"sbb-sort-header-arrow\"\n      [@arrowOpacity]=\"_getArrowViewState()\"\n      [@arrowPosition]=\"_getArrowViewState()\"\n      [@allowChildren]=\"_getArrowDirectionState()\"\n      (@arrowPosition.start)=\"_disableViewStateAnimation = true\"\n      (@arrowPosition.done)=\"_disableViewStateAnimation = false\"\n    >\n      <!-- This is a modified version of the sbb arrow-down-small icon to support the animation -->\n      <svg\n        xmlns=\"http://www.w3.org/2000/svg\"\n        width=\"24\"\n        height=\"24\"\n        viewBox=\"0 0 24 24\"\n        preserveAspectRatio=\"xMidYMid meet\"\n        focusable=\"false\"\n      >\n        <path\n          fill=\"none\"\n          fill-rule=\"evenodd\"\n          stroke=\"currentColor\"\n          stroke-width=\"1\"\n          d=\"M11.5,5.75 L11.5,18.25\"\n        ></path>\n        <path\n          fill=\"none\"\n          fill-rule=\"evenodd\"\n          stroke=\"currentColor\"\n          stroke-width=\"1\"\n          d=\"M7.5,14.25 L11.5,18.25 L15.5,14.25\"\n          style=\"transform-origin: center\"\n          [@indicator]=\"_getArrowDirectionState()\"\n        ></path>\n      </svg>\n    </div>\n  }\n</div>\n", styles: [".sbb-sort-header{cursor:pointer;position:relative}.sbb-sort-header:is(.cdk-keyboard-focused,.cdk-program-focused){outline:var(--sbb-border-width-thin) solid var(--sbb-color-weak-accent);outline-offset:var(--sbb-border-width-thin-negative)}.sbb-sort-header:hover .sbb-sort-header-container{color:var(--sbb-color-call-to-action-hover)}.sbb-sort-header-sorted{color:var(--sbb-color-text)}.sbb-sort-header-container{display:flex;letter-spacing:normal;text-align:left;height:100%;outline:0}.sbb-sort-header-disabled .sbb-sort-header-container{cursor:default}.sbb-table-align-center .sbb-sort-header-container{text-align:center}.sbb-sort-header-content{flex-shrink:1;flex-grow:1}.sbb-sort-header-position-before{flex-direction:row-reverse}.sbb-sort-header-arrow{height:var(--sbb-icon-size-default);width:var(--sbb-icon-size-default);min-width:var(--sbb-icon-size-default);position:relative;opacity:0;margin:calc(-0.0666666667rem*var(--sbb-scaling-factor)) calc(-0.5333333333rem*var(--sbb-scaling-factor)) calc(-0.0666666667rem*var(--sbb-scaling-factor)) var(--sbb-table-sort-header-arrow-margin)}.sbb-sort-header-position-before .sbb-sort-header-arrow{margin:calc(-0.0666666667rem*var(--sbb-scaling-factor)) var(--sbb-table-sort-header-arrow-margin) calc(-0.0666666667rem*var(--sbb-scaling-factor)) calc(-0.0666666667rem*var(--sbb-scaling-factor))}.sbb-sort-header-arrow svg{width:100%;height:100%}"] }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }, { type: SbbSort, decorators: [{
                    type: Optional
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['SBB_SORT_HEADER_COLUMN_DEF']
                }, {
                    type: Optional
                }] }, { type: i3.CdkColumnDef, decorators: [{
                    type: Inject,
                    args: ['MAT_SORT_HEADER_COLUMN_DEF']
                }, {
                    type: Optional
                }] }, { type: i2.FocusMonitor }, { type: i0.ElementRef }, { type: i2.AriaDescriber }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [SBB_SORT_DEFAULT_OPTIONS]
                }] }], propDecorators: { id: [{
                type: Input,
                args: ['sbb-sort-header']
            }], arrowPosition: [{
                type: Input
            }], disabled: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], start: [{
                type: Input
            }], sortActionDescription: [{
                type: Input
            }], disableClear: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }] } });

/**
 * Cell definition for the sbb-table.
 * Captures the template of a column's data row cell as well as cell-specific properties.
 */
class SbbCellDef extends CdkCellDef {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbCellDef, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.7", type: SbbCellDef, isStandalone: true, selector: "[sbbCellDef]", providers: [{ provide: CdkCellDef, useExisting: SbbCellDef }], usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbCellDef, decorators: [{
            type: Directive,
            args: [{
                    selector: '[sbbCellDef]',
                    providers: [{ provide: CdkCellDef, useExisting: SbbCellDef }],
                    standalone: true,
                }]
        }] });
/**
 * Header cell definition for the sbb-table.
 * Captures the template of a column's header cell and as well as cell-specific properties.
 */
class SbbHeaderCellDef extends CdkHeaderCellDef {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbHeaderCellDef, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.7", type: SbbHeaderCellDef, isStandalone: true, selector: "[sbbHeaderCellDef]", providers: [{ provide: CdkHeaderCellDef, useExisting: SbbHeaderCellDef }], usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbHeaderCellDef, decorators: [{
            type: Directive,
            args: [{
                    selector: '[sbbHeaderCellDef]',
                    providers: [{ provide: CdkHeaderCellDef, useExisting: SbbHeaderCellDef }],
                    standalone: true,
                }]
        }] });
/**
 * Footer cell definition for the sbb-table.
 * Captures the template of a column's footer cell and as well as cell-specific properties.
 */
class SbbFooterCellDef extends CdkFooterCellDef {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbFooterCellDef, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.7", type: SbbFooterCellDef, isStandalone: true, selector: "[sbbFooterCellDef]", providers: [{ provide: CdkFooterCellDef, useExisting: SbbFooterCellDef }], usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbFooterCellDef, decorators: [{
            type: Directive,
            args: [{
                    selector: '[sbbFooterCellDef]',
                    providers: [{ provide: CdkFooterCellDef, useExisting: SbbFooterCellDef }],
                    standalone: true,
                }]
        }] });
/**
 * Column definition for the sbb-table.
 * Defines a set of cells available for a table column.
 */
class SbbColumnDef extends CdkColumnDef {
    constructor() {
        super(...arguments);
        this._groupWithNext = false;
    }
    /** Unique name for this column. */
    get name() {
        return this._name;
    }
    set name(name) {
        this._setNameInput(name);
    }
    /**
     * Group this column with the next column.
     * If set to true, the border to the next cell is hidden.
     */
    get groupWithNext() {
        return this._groupWithNext;
    }
    set groupWithNext(value) {
        this._groupWithNext = coerceBooleanProperty(value);
    }
    /**
     * Add "sbb-column-" prefix in addition to "cdk-column-" prefix.
     * In the future, this will only add "sbb-column-" and columnCssClassName
     * will change from type string[] to string.
     * @docs-private
     */
    _updateColumnCssClassName() {
        super._updateColumnCssClassName();
        this._columnCssClassName.push(`sbb-column-${this.cssClassFriendlyName}`);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbColumnDef, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.7", type: SbbColumnDef, isStandalone: true, selector: "[sbbColumnDef]", inputs: { name: ["sbbColumnDef", "name"], groupWithNext: "groupWithNext" }, providers: [
            { provide: CdkColumnDef, useExisting: SbbColumnDef },
            { provide: 'SBB_SORT_HEADER_COLUMN_DEF', useExisting: SbbColumnDef },
        ], usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbColumnDef, decorators: [{
            type: Directive,
            args: [{
                    selector: '[sbbColumnDef]',
                    providers: [
                        { provide: CdkColumnDef, useExisting: SbbColumnDef },
                        { provide: 'SBB_SORT_HEADER_COLUMN_DEF', useExisting: SbbColumnDef },
                    ],
                    standalone: true,
                }]
        }], propDecorators: { name: [{
                type: Input,
                args: ['sbbColumnDef']
            }], groupWithNext: [{
                type: Input
            }] } });
/** Header cell template container that adds the right classes and role. */
class SbbHeaderCell extends CdkHeaderCell {
    constructor(_columnDef, elementRef) {
        super(_columnDef, elementRef);
        this._columnDef = _columnDef;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbHeaderCell, deps: [{ token: SbbColumnDef }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.7", type: SbbHeaderCell, isStandalone: true, selector: "sbb-header-cell, th[sbb-header-cell]", host: { attributes: { "role": "columnheader" }, properties: { "class.sbb-table-group-with-next": "_columnDef.groupWithNext" }, classAttribute: "sbb-header-cell" }, usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbHeaderCell, decorators: [{
            type: Directive,
            args: [{
                    selector: 'sbb-header-cell, th[sbb-header-cell]',
                    host: {
                        class: 'sbb-header-cell',
                        role: 'columnheader',
                        '[class.sbb-table-group-with-next]': '_columnDef.groupWithNext',
                    },
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: SbbColumnDef }, { type: i0.ElementRef }] });
/** Footer cell template container that adds the right classes and role. */
class SbbFooterCell extends CdkFooterCell {
    constructor(_columnDef, elementRef) {
        super(_columnDef, elementRef);
        this._columnDef = _columnDef;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbFooterCell, deps: [{ token: SbbColumnDef }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.7", type: SbbFooterCell, isStandalone: true, selector: "sbb-footer-cell, td[sbb-footer-cell]", host: { attributes: { "role": "gridcell" }, properties: { "class.sbb-table-group-with-next": "_columnDef.groupWithNext" }, classAttribute: "sbb-footer-cell" }, usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbFooterCell, decorators: [{
            type: Directive,
            args: [{
                    selector: 'sbb-footer-cell, td[sbb-footer-cell]',
                    host: {
                        class: 'sbb-footer-cell',
                        role: 'gridcell',
                        '[class.sbb-table-group-with-next]': '_columnDef.groupWithNext',
                    },
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: SbbColumnDef }, { type: i0.ElementRef }] });
/** Cell template container that adds the right classes and role. */
class SbbCell extends CdkCell {
    constructor(_columnDef, elementRef) {
        super(_columnDef, elementRef);
        this._columnDef = _columnDef;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbCell, deps: [{ token: SbbColumnDef }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.7", type: SbbCell, isStandalone: true, selector: "sbb-cell, td[sbb-cell]", host: { attributes: { "role": "gridcell" }, properties: { "class.sbb-table-group-with-next": "_columnDef.groupWithNext" }, classAttribute: "sbb-cell" }, usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbCell, decorators: [{
            type: Directive,
            args: [{
                    selector: 'sbb-cell, td[sbb-cell]',
                    host: {
                        class: 'sbb-cell',
                        role: 'gridcell',
                        '[class.sbb-table-group-with-next]': '_columnDef.groupWithNext',
                    },
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: SbbColumnDef }, { type: i0.ElementRef }] });

// We can't reuse `CDK_ROW_TEMPLATE` because it's incompatible with local compilation mode.
const ROW_TEMPLATE = `<ng-container cdkCellOutlet></ng-container>`;
/**
 * Header row definition for the sbb-table.
 * Captures the header row's template and other header properties such as the columns to display.
 */
class SbbHeaderRowDef extends CdkHeaderRowDef {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbHeaderRowDef, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "16.1.0", version: "17.3.7", type: SbbHeaderRowDef, isStandalone: true, selector: "[sbbHeaderRowDef]", inputs: { columns: ["sbbHeaderRowDef", "columns"], sticky: ["sbbHeaderRowDefSticky", "sticky", booleanAttribute] }, providers: [{ provide: CdkHeaderRowDef, useExisting: SbbHeaderRowDef }], usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbHeaderRowDef, decorators: [{
            type: Directive,
            args: [{
                    selector: '[sbbHeaderRowDef]',
                    providers: [{ provide: CdkHeaderRowDef, useExisting: SbbHeaderRowDef }],
                    inputs: [
                        { name: 'columns', alias: 'sbbHeaderRowDef' },
                        { name: 'sticky', alias: 'sbbHeaderRowDefSticky', transform: booleanAttribute },
                    ],
                    standalone: true,
                }]
        }] });
/**
 * Footer row definition for the sbb-table.
 * Captures the footer row's template and other footer properties such as the columns to display.
 */
class SbbFooterRowDef extends CdkFooterRowDef {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbFooterRowDef, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "16.1.0", version: "17.3.7", type: SbbFooterRowDef, isStandalone: true, selector: "[sbbFooterRowDef]", inputs: { columns: ["sbbFooterRowDef", "columns"], sticky: ["sbbFooterRowDefSticky", "sticky", booleanAttribute] }, providers: [{ provide: CdkFooterRowDef, useExisting: SbbFooterRowDef }], usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbFooterRowDef, decorators: [{
            type: Directive,
            args: [{
                    selector: '[sbbFooterRowDef]',
                    providers: [{ provide: CdkFooterRowDef, useExisting: SbbFooterRowDef }],
                    inputs: [
                        { name: 'columns', alias: 'sbbFooterRowDef' },
                        { name: 'sticky', alias: 'sbbFooterRowDefSticky', transform: booleanAttribute },
                    ],
                    standalone: true,
                }]
        }] });
/**
 * Data row definition for the sbb-table.
 * Captures the data row's template and other properties such as the columns to display and
 * a when predicate that describes when this row should be used.
 */
class SbbRowDef extends CdkRowDef {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbRowDef, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.7", type: SbbRowDef, isStandalone: true, selector: "[sbbRowDef]", inputs: { columns: ["sbbRowDefColumns", "columns"], when: ["sbbRowDefWhen", "when"] }, providers: [{ provide: CdkRowDef, useExisting: SbbRowDef }], usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbRowDef, decorators: [{
            type: Directive,
            args: [{
                    selector: '[sbbRowDef]',
                    providers: [{ provide: CdkRowDef, useExisting: SbbRowDef }],
                    inputs: [
                        { name: 'columns', alias: 'sbbRowDefColumns' },
                        { name: 'when', alias: 'sbbRowDefWhen' },
                    ],
                    standalone: true,
                }]
        }] });
/** Header template container that contains the cell outlet. Adds the right class and role. */
class SbbHeaderRow extends CdkHeaderRow {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbHeaderRow, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.7", type: SbbHeaderRow, isStandalone: true, selector: "sbb-header-row, tr[sbb-header-row]", host: { attributes: { "role": "row" }, classAttribute: "sbb-header-row" }, providers: [{ provide: CdkHeaderRow, useExisting: SbbHeaderRow }], exportAs: ["sbbHeaderRow"], usesInheritance: true, ngImport: i0, template: "<ng-container cdkCellOutlet></ng-container>", isInline: true, dependencies: [{ kind: "directive", type: CdkCellOutlet, selector: "[cdkCellOutlet]" }], changeDetection: i0.ChangeDetectionStrategy.Default, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbHeaderRow, decorators: [{
            type: Component,
            args: [{
                    selector: 'sbb-header-row, tr[sbb-header-row]',
                    template: ROW_TEMPLATE,
                    host: {
                        class: 'sbb-header-row',
                        role: 'row',
                    },
                    // See note on CdkTable for explanation on why this uses the default change detection strategy.
                    // tslint:disable-next-line:validate-decorators
                    changeDetection: ChangeDetectionStrategy.Default,
                    encapsulation: ViewEncapsulation.None,
                    exportAs: 'sbbHeaderRow',
                    providers: [{ provide: CdkHeaderRow, useExisting: SbbHeaderRow }],
                    standalone: true,
                    imports: [CdkCellOutlet],
                }]
        }] });
/** Footer template container that contains the cell outlet. Adds the right class and role. */
class SbbFooterRow extends CdkFooterRow {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbFooterRow, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.7", type: SbbFooterRow, isStandalone: true, selector: "sbb-footer-row, tr[sbb-footer-row]", host: { attributes: { "role": "row" }, classAttribute: "sbb-footer-row" }, providers: [{ provide: CdkFooterRow, useExisting: SbbFooterRow }], exportAs: ["sbbFooterRow"], usesInheritance: true, ngImport: i0, template: "<ng-container cdkCellOutlet></ng-container>", isInline: true, dependencies: [{ kind: "directive", type: CdkCellOutlet, selector: "[cdkCellOutlet]" }], changeDetection: i0.ChangeDetectionStrategy.Default, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbFooterRow, decorators: [{
            type: Component,
            args: [{
                    selector: 'sbb-footer-row, tr[sbb-footer-row]',
                    template: ROW_TEMPLATE,
                    host: {
                        class: 'sbb-footer-row',
                        role: 'row',
                    },
                    // See note on CdkTable for explanation on why this uses the default change detection strategy.
                    // tslint:disable-next-line:validate-decorators
                    changeDetection: ChangeDetectionStrategy.Default,
                    encapsulation: ViewEncapsulation.None,
                    exportAs: 'sbbFooterRow',
                    providers: [{ provide: CdkFooterRow, useExisting: SbbFooterRow }],
                    standalone: true,
                    imports: [CdkCellOutlet],
                }]
        }] });
/** Data row template container that contains the cell outlet. Adds the right class and role. */
class SbbRow extends CdkRow {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbRow, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.7", type: SbbRow, isStandalone: true, selector: "sbb-row, tr[sbb-row]", host: { attributes: { "role": "row" }, classAttribute: "sbb-row" }, providers: [{ provide: CdkRow, useExisting: SbbRow }], exportAs: ["sbbRow"], usesInheritance: true, ngImport: i0, template: "<ng-container cdkCellOutlet></ng-container>", isInline: true, dependencies: [{ kind: "directive", type: CdkCellOutlet, selector: "[cdkCellOutlet]" }], changeDetection: i0.ChangeDetectionStrategy.Default, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbRow, decorators: [{
            type: Component,
            args: [{
                    selector: 'sbb-row, tr[sbb-row]',
                    template: ROW_TEMPLATE,
                    host: {
                        class: 'sbb-row',
                        role: 'row',
                    },
                    // See note on CdkTable for explanation on why this uses the default change detection strategy.
                    // tslint:disable-next-line:validate-decorators
                    changeDetection: ChangeDetectionStrategy.Default,
                    encapsulation: ViewEncapsulation.None,
                    exportAs: 'sbbRow',
                    providers: [{ provide: CdkRow, useExisting: SbbRow }],
                    standalone: true,
                    imports: [CdkCellOutlet],
                }]
        }] });

/**
 * Enables the recycle view repeater strategy, which reduces rendering latency. Not compatible with
 * tables that animate rows.
 */
class SbbRecycleRows {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbRecycleRows, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.7", type: SbbRecycleRows, isStandalone: true, selector: "sbb-table[recycleRows], table[sbb-table][recycleRows]", providers: [{ provide: _VIEW_REPEATER_STRATEGY, useClass: _RecycleViewRepeaterStrategy }], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbRecycleRows, decorators: [{
            type: Directive,
            args: [{
                    selector: 'sbb-table[recycleRows], table[sbb-table][recycleRows]',
                    providers: [{ provide: _VIEW_REPEATER_STRATEGY, useClass: _RecycleViewRepeaterStrategy }],
                    standalone: true,
                }]
        }] });
/**
 * Wrapper for the CdkTable with Sbb design styles.
 */
class SbbTable extends CdkTable {
    constructor(differs, changeDetectorRef, elementRef, role, dir, document, platform, viewRepeater, coalescedStyleScheduler, _viewportRulerSbb, stickyPositioningListener, ngZone) {
        super(differs, changeDetectorRef, elementRef, role, dir, document, platform, viewRepeater, coalescedStyleScheduler, _viewportRulerSbb, stickyPositioningListener, ngZone);
        this._viewportRulerSbb = _viewportRulerSbb;
        /** Overrides the sticky CSS class set by the `CdkTable`. */
        // tslint:disable-next-line:naming-convention
        this.stickyCssClass = 'sbb-table-sticky';
        /** Overrides the need to add position: sticky on every sticky cell element in `CdkTable`. */
        // tslint:disable-next-line:naming-convention
        this.needsPositionStickyOnElement = false;
        this._destroyed = new Subject();
    }
    ngOnInit() {
        super.ngOnInit();
        // If more than one column is sticky, the left offset is calculated at a wrong
        // time by cdk and sticky columns can get overlapped.
        // This workaround calculates sticky styles whenever the viewport has changed
        // using a Promise.resolve() to postpone data calculation to the time the content is already placed in DOM.
        // See also https://github.com/angular/components/issues/15885.
        this._ngZone.runOutsideAngular(() => {
            this._viewportRulerSbb
                .change(150)
                .pipe(takeUntil(this._destroyed))
                .subscribe(() => {
                Promise.resolve().then(() => {
                    this.updateStickyColumnStyles();
                });
            });
        });
    }
    ngOnDestroy() {
        super.ngOnDestroy();
        this._destroyed.next();
        this._destroyed.complete();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbTable, deps: [{ token: i0.IterableDiffers }, { token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: 'role', attribute: true }, { token: i1.Directionality, optional: true }, { token: DOCUMENT }, { token: i2$1.Platform }, { token: _VIEW_REPEATER_STRATEGY }, { token: _COALESCED_STYLE_SCHEDULER }, { token: i3$1.ViewportRuler }, { token: STICKY_POSITIONING_LISTENER, optional: true, skipSelf: true }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "17.3.7", type: SbbTable, isStandalone: true, selector: "sbb-table, table[sbb-table]", host: { properties: { "class.sbb-table-fixed-layout": "fixedLayout" }, classAttribute: "sbb-table" }, providers: [
            // TODO(michaeljamesparsons) Abstract the view repeater strategy to a directive API so this code
            //  is only included in the build if used.
            { provide: _VIEW_REPEATER_STRATEGY, useClass: _DisposeViewRepeaterStrategy },
            { provide: CdkTable, useExisting: SbbTable },
            { provide: CDK_TABLE, useExisting: SbbTable },
            { provide: _COALESCED_STYLE_SCHEDULER, useClass: _CoalescedStyleScheduler },
            // Prevent nested tables from seeing this table's StickyPositioningListener.
            { provide: STICKY_POSITIONING_LISTENER, useValue: null },
        ], exportAs: ["sbbTable"], usesInheritance: true, ngImport: i0, template: `
    <ng-content select="caption" />
    <ng-content select="colgroup, col" />
    <!--
        Unprojected content throws a hydration error so we need this to capture it.
        It gets removed on the client so it doesn't affect the layout.
      -->
    @if (_isServer) {
      <ng-content />
    }
    @if (_isNativeHtmlTable) {
      <thead role="rowgroup">
        <ng-container headerRowOutlet />
      </thead>
      <tbody role="rowgroup">
        <ng-container rowOutlet />
        <ng-container noDataRowOutlet />
      </tbody>
      <tfoot role="rowgroup">
        <ng-container footerRowOutlet />
      </tfoot>
    } @else {
      <ng-container headerRowOutlet />
      <ng-container rowOutlet />
      <ng-container noDataRowOutlet />
      <ng-container footerRowOutlet />
    }
  `, isInline: true, dependencies: [{ kind: "directive", type: HeaderRowOutlet, selector: "[headerRowOutlet]" }, { kind: "directive", type: DataRowOutlet, selector: "[rowOutlet]" }, { kind: "directive", type: NoDataRowOutlet, selector: "[noDataRowOutlet]" }, { kind: "directive", type: FooterRowOutlet, selector: "[footerRowOutlet]" }], changeDetection: i0.ChangeDetectionStrategy.Default, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbTable, decorators: [{
            type: Component,
            args: [{
                    selector: 'sbb-table, table[sbb-table]',
                    exportAs: 'sbbTable',
                    // Note that according to MDN, the `caption` element has to be projected as the **first**
                    // element in the table. See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/caption
                    // We can't reuse `CDK_TABLE_TEMPLATE` because it's incompatible with local compilation mode.
                    template: `
    <ng-content select="caption" />
    <ng-content select="colgroup, col" />
    <!--
        Unprojected content throws a hydration error so we need this to capture it.
        It gets removed on the client so it doesn't affect the layout.
      -->
    @if (_isServer) {
      <ng-content />
    }
    @if (_isNativeHtmlTable) {
      <thead role="rowgroup">
        <ng-container headerRowOutlet />
      </thead>
      <tbody role="rowgroup">
        <ng-container rowOutlet />
        <ng-container noDataRowOutlet />
      </tbody>
      <tfoot role="rowgroup">
        <ng-container footerRowOutlet />
      </tfoot>
    } @else {
      <ng-container headerRowOutlet />
      <ng-container rowOutlet />
      <ng-container noDataRowOutlet />
      <ng-container footerRowOutlet />
    }
  `,
                    host: {
                        class: 'sbb-table',
                        '[class.sbb-table-fixed-layout]': 'fixedLayout',
                    },
                    providers: [
                        // TODO(michaeljamesparsons) Abstract the view repeater strategy to a directive API so this code
                        //  is only included in the build if used.
                        { provide: _VIEW_REPEATER_STRATEGY, useClass: _DisposeViewRepeaterStrategy },
                        { provide: CdkTable, useExisting: SbbTable },
                        { provide: CDK_TABLE, useExisting: SbbTable },
                        { provide: _COALESCED_STYLE_SCHEDULER, useClass: _CoalescedStyleScheduler },
                        // Prevent nested tables from seeing this table's StickyPositioningListener.
                        { provide: STICKY_POSITIONING_LISTENER, useValue: null },
                    ],
                    encapsulation: ViewEncapsulation.None,
                    // See note on CdkTable for explanation on why this uses the default change detection strategy.
                    // tslint:disable-next-line:validate-decorators
                    changeDetection: ChangeDetectionStrategy.Default,
                    standalone: true,
                    imports: [HeaderRowOutlet, DataRowOutlet, NoDataRowOutlet, FooterRowOutlet],
                }]
        }], ctorParameters: () => [{ type: i0.IterableDiffers }, { type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: undefined, decorators: [{
                    type: Attribute,
                    args: ['role']
                }] }, { type: i1.Directionality, decorators: [{
                    type: Optional
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i2$1.Platform }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [_VIEW_REPEATER_STRATEGY]
                }] }, { type: i3._CoalescedStyleScheduler, decorators: [{
                    type: Inject,
                    args: [_COALESCED_STYLE_SCHEDULER]
                }] }, { type: i3$1.ViewportRuler }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: SkipSelf
                }, {
                    type: Inject,
                    args: [STICKY_POSITIONING_LISTENER]
                }] }, { type: i0.NgZone }] });

/** Config used to bind passive event listeners */
const passiveEventListenerOptions = normalizePassiveListenerOptions({
    passive: true,
});
class SbbTableWrapper {
    /** Whether the table wrapper is focusable. */
    get focusable() {
        return this._focusable;
    }
    set focusable(value) {
        this._focusable = coerceBooleanProperty(value);
    }
    constructor(_elementRef, _ngZone, _viewportRuler) {
        this._elementRef = _elementRef;
        this._ngZone = _ngZone;
        this._viewportRuler = _viewportRuler;
        this._destroyed = new Subject();
        this._focusable = true;
    }
    ngAfterViewInit() {
        const resize = this._viewportRuler.change(150);
        this._ngZone.runOutsideAngular(() => {
            merge(fromEvent(this._elementRef.nativeElement, 'scroll', passiveEventListenerOptions), resize)
                .pipe(startWith(null), map(() => this._calculateScrollOffset()), distinctUntilChanged(), takeUntil(this._destroyed))
                .subscribe((state) => {
                this._elementRef.nativeElement.classList.remove(`sbb-table-wrapper-offset-none`, `sbb-table-wrapper-offset-left`, `sbb-table-wrapper-offset-right`, `sbb-table-wrapper-offset-both`);
                this._elementRef.nativeElement.classList.add(`sbb-table-wrapper-offset-${state}`);
            });
        });
    }
    /**
     * Calculate whether the scroll offset is none, left, right or on both sides.
     */
    _calculateScrollOffset() {
        const element = this._elementRef.nativeElement;
        if (element.scrollWidth === element.offsetWidth) {
            return 'none';
        }
        const isAtStart = element.scrollLeft === 0;
        // In some cases the combined value of scrollLeft and offsetWidth is off by
        // 1 pixel from the scrollWidth.
        const isAtEnd = element.scrollWidth - element.scrollLeft - element.offsetWidth <= 1;
        if (isAtStart) {
            return isAtEnd ? 'none' : 'right';
        }
        return isAtEnd ? 'left' : 'both';
    }
    ngOnDestroy() {
        this._destroyed.next();
        this._destroyed.complete();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbTableWrapper, deps: [{ token: i0.ElementRef }, { token: i0.NgZone }, { token: i3$1.ViewportRuler }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.7", type: SbbTableWrapper, isStandalone: true, selector: "sbb-table-wrapper", inputs: { focusable: "focusable" }, host: { attributes: { "role": "section" }, properties: { "attr.tabindex": "focusable ? 0 : null" }, classAttribute: "sbb-table-wrapper sbb-scrollbar" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbTableWrapper, decorators: [{
            type: Directive,
            args: [{
                    selector: 'sbb-table-wrapper',
                    host: {
                        class: 'sbb-table-wrapper sbb-scrollbar',
                        '[attr.tabindex]': 'focusable ? 0 : null',
                        role: 'section',
                    },
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.NgZone }, { type: i3$1.ViewportRuler }], propDecorators: { focusable: [{
                type: Input
            }] } });

/**
 * Column that simply shows text content for the header and row cells. Assumes that the table
 * is using the native table implementation (`<table>`).
 *
 * By default, the name of this column will be the header text and data property accessor.
 * The header text can be overridden with the `headerText` input. Cell values can be overridden with
 * the `dataAccessor` input. Change the text justification to the start or end using the `justify`
 * input.
 */
class SbbTextColumn extends CdkTextColumn {
    constructor() {
        super(...arguments);
        this._groupWithNext = false;
    }
    /**
     * Group this column with the next column.
     * If set to true, the border to the next cell is hidden.
     */
    get groupWithNext() {
        return this._groupWithNext;
    }
    set groupWithNext(value) {
        this._groupWithNext = coerceBooleanProperty(value);
        // With Ivy, inputs can be initialized before static query results are
        // available. In that case, we defer the synchronization until "ngOnInit" fires.
        this._syncColumnDefGroupWithNext();
    }
    ngOnInit() {
        super.ngOnInit();
        this._syncColumnDefGroupWithNext();
    }
    /** Synchronizes the column definition groupWithNext with the text column groupWithNext. */
    _syncColumnDefGroupWithNext() {
        if (this.columnDef) {
            this.columnDef.groupWithNext = this._groupWithNext;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbTextColumn, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.7", type: SbbTextColumn, isStandalone: true, selector: "sbb-text-column", inputs: { groupWithNext: "groupWithNext" }, usesInheritance: true, ngImport: i0, template: `
    <ng-container sbbColumnDef>
      <th sbb-header-cell *sbbHeaderCellDef [style.text-align]="justify">
        {{ headerText }}
      </th>
      <td sbb-cell *sbbCellDef="let data" [style.text-align]="justify">
        {{ dataAccessor(data, name) }}
      </td>
    </ng-container>
  `, isInline: true, dependencies: [{ kind: "directive", type: SbbColumnDef, selector: "[sbbColumnDef]", inputs: ["sbbColumnDef", "groupWithNext"] }, { kind: "directive", type: SbbHeaderCellDef, selector: "[sbbHeaderCellDef]" }, { kind: "directive", type: SbbHeaderCell, selector: "sbb-header-cell, th[sbb-header-cell]" }, { kind: "directive", type: SbbCellDef, selector: "[sbbCellDef]" }, { kind: "directive", type: SbbCell, selector: "sbb-cell, td[sbb-cell]" }], changeDetection: i0.ChangeDetectionStrategy.Default, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbTextColumn, decorators: [{
            type: Component,
            args: [{
                    selector: 'sbb-text-column',
                    template: `
    <ng-container sbbColumnDef>
      <th sbb-header-cell *sbbHeaderCellDef [style.text-align]="justify">
        {{ headerText }}
      </th>
      <td sbb-cell *sbbCellDef="let data" [style.text-align]="justify">
        {{ dataAccessor(data, name) }}
      </td>
    </ng-container>
  `,
                    encapsulation: ViewEncapsulation.None,
                    // Change detection is intentionally not set to OnPush. This component's template will be provided
                    // to the table to be inserted into its view. This is problematic when change detection runs since
                    // the bindings in this template will be evaluated _after_ the table's view is evaluated, which
                    // mean's the template in the table's view will not have the updated value (and in fact will cause
                    // an ExpressionChangedAfterItHasBeenCheckedError).
                    // tslint:disable-next-line:validate-decorators
                    changeDetection: ChangeDetectionStrategy.Default,
                    standalone: true,
                    imports: [SbbColumnDef, SbbHeaderCellDef, SbbHeaderCell, SbbCellDef, SbbCell],
                }]
        }], propDecorators: { groupWithNext: [{
                type: Input
            }] } });

const EXPORTED_DECLARATIONS = [
    // Table
    SbbTable,
    SbbRecycleRows,
    SbbTableWrapper,
    // Template defs
    SbbHeaderCellDef,
    SbbHeaderRowDef,
    SbbColumnDef,
    SbbCellDef,
    SbbRowDef,
    SbbFooterCellDef,
    SbbFooterRowDef,
    // Cell directives
    SbbHeaderCell,
    SbbCell,
    SbbFooterCell,
    // Row directives
    SbbHeaderRow,
    SbbRow,
    SbbFooterRow,
    SbbTextColumn,
    // Sort
    SbbSort,
    SbbSortHeader,
];
class SbbTableModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbTableModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.7", ngImport: i0, type: SbbTableModule, imports: [CdkTableModule, SbbCommonModule, 
            // Table
            SbbTable,
            SbbRecycleRows,
            SbbTableWrapper,
            // Template defs
            SbbHeaderCellDef,
            SbbHeaderRowDef,
            SbbColumnDef,
            SbbCellDef,
            SbbRowDef,
            SbbFooterCellDef,
            SbbFooterRowDef,
            // Cell directives
            SbbHeaderCell,
            SbbCell,
            SbbFooterCell,
            // Row directives
            SbbHeaderRow,
            SbbRow,
            SbbFooterRow,
            SbbTextColumn,
            // Sort
            SbbSort,
            SbbSortHeader], exports: [
            // Table
            SbbTable,
            SbbRecycleRows,
            SbbTableWrapper,
            // Template defs
            SbbHeaderCellDef,
            SbbHeaderRowDef,
            SbbColumnDef,
            SbbCellDef,
            SbbRowDef,
            SbbFooterCellDef,
            SbbFooterRowDef,
            // Cell directives
            SbbHeaderCell,
            SbbCell,
            SbbFooterCell,
            // Row directives
            SbbHeaderRow,
            SbbRow,
            SbbFooterRow,
            SbbTextColumn,
            // Sort
            SbbSort,
            SbbSortHeader] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbTableModule, imports: [CdkTableModule, SbbCommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbTableModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CdkTableModule, SbbCommonModule, ...EXPORTED_DECLARATIONS],
                    exports: EXPORTED_DECLARATIONS,
                }]
        }] });

/**
 * Corresponds to `Number.MAX_SAFE_INTEGER`. Moved out into a variable here due to
 * flaky browser support and the value not being defined in Closure's typings.
 */
const MAX_SAFE_INTEGER = 9007199254740991;
/** Base class for SbbTableDataSource. */
// tslint:disable-next-line:class-name naming-convention
class _SbbTableDataSource extends DataSource {
    /** Array of data that should be rendered by the table, where each object represents one row. */
    get data() {
        return this._data.value;
    }
    set data(data) {
        data = Array.isArray(data) ? data : [];
        this._data.next(data);
        // Normally the `filteredData` is updated by the re-render
        // subscription, but that won't happen if it's inactive.
        if (!this._renderChangesSubscription) {
            this._filterData(data);
        }
    }
    /**
     * Filter term that should be used to filter out objects from the data array. To override how
     * data objects match to this filter string, provide a custom function for filterPredicate.
     */
    get filter() {
        return this._filter.value;
    }
    set filter(filter) {
        this._filter.next(filter);
        // Normally the `filteredData` is updated by the re-render
        // subscription, but that won't happen if it's inactive.
        if (!this._renderChangesSubscription) {
            this._filterData(this.data);
        }
    }
    /**
     * Instance of the SbbSort directive used by the table to control its sorting. Sort changes
     * emitted by the SbbSort will trigger an update to the table's rendered data.
     */
    get sort() {
        return this._sort;
    }
    set sort(sort) {
        this._sort = sort;
        this._updateChangeSubscription();
    }
    /**
     * Instance of the SbbPaginator component used by the table to control what page of the data is
     * displayed. Page changes emitted by the SbbPaginator will trigger an update to the
     * table's rendered data.
     *
     * Note that the data source uses the paginator's properties to calculate which page of data
     * should be displayed. If the paginator receives its properties as template inputs,
     * e.g. `[pageLength]=100` or `[pageIndex]=1`, then be sure that the paginator's view has been
     * initialized before assigning it to this data source.
     */
    get paginator() {
        return this._paginator;
    }
    set paginator(paginator) {
        this._paginator = paginator;
        this._updateChangeSubscription();
    }
    constructor(initialData = []) {
        super();
        /** Stream emitting render data to the table (depends on ordered data changes). */
        this._renderData = new BehaviorSubject([]);
        /** Stream that emits when a new filter string is set on the data source. */
        this._filter = new BehaviorSubject(null);
        /** Used to react to internal changes of the paginator that are made by the data source itself. */
        this._internalPageChanges = new Subject();
        /**
         * Subscription to the changes that should trigger an update to the table's rendered rows, such
         * as filtering, sorting, pagination, or base data changes.
         */
        this._renderChangesSubscription = null;
        /**
         * Data accessor function that is used for accessing data properties for sorting through
         * the default sortData function.
         * This default function assumes that the sort header IDs (which defaults to the column name)
         * matches the data's properties (e.g. column Xyz represents data['Xyz']).
         * May be set to a custom function for different behavior.
         * @param data Data object that is being accessed.
         * @param sortHeaderId The name of the column that represents the data.
         */
        this.sortingDataAccessor = (data, sortHeaderId) => {
            const value = data[sortHeaderId];
            if (_isNumberValue(value)) {
                const numberValue = Number(value);
                // Numbers beyond `MAX_SAFE_INTEGER` can't be compared reliably so we
                // leave them as strings. For more info: https://goo.gl/y5vbSg
                return numberValue < MAX_SAFE_INTEGER ? numberValue : value;
            }
            return value;
        };
        /**
         * Gets a sorted copy of the data array based on the state of the SbbSort. Called
         * after changes are made to the filtered data or when sort changes are emitted from SbbSort.
         * By default, the function retrieves the active sort and its direction and compares data
         * by retrieving data using the sortingDataAccessor. May be overridden for a custom implementation
         * of data ordering.
         * @param data The array of data that should be sorted.
         * @param sort The connected SbbSort that holds the current sort state.
         */
        this.sortData = (data, sort) => {
            const active = sort.active;
            const direction = sort.direction;
            if (!active || direction === '') {
                return data;
            }
            return data.sort((a, b) => {
                let valueA = this.sortingDataAccessor(a, active);
                let valueB = this.sortingDataAccessor(b, active);
                // If there are data in the column that can be converted to a number,
                // it must be ensured that the rest of the data
                // is of the same type so as not to order incorrectly.
                const valueAType = typeof valueA;
                const valueBType = typeof valueB;
                if (valueAType !== valueBType) {
                    if (valueAType === 'number') {
                        valueA += '';
                    }
                    if (valueBType === 'number') {
                        valueB += '';
                    }
                }
                // If both valueA and valueB exist (truthy), then compare the two. Otherwise, check if
                // one value exists while the other doesn't. In this case, existing value should come last.
                // This avoids inconsistent results when comparing values to undefined/null.
                // If neither value exists, return 0 (equal).
                let comparatorResult = 0;
                if (valueA != null && valueB != null) {
                    // Check if one value is greater than the other; if equal, comparatorResult should remain 0.
                    if (valueA > valueB) {
                        comparatorResult = 1;
                    }
                    else if (valueA < valueB) {
                        comparatorResult = -1;
                    }
                }
                else if (valueA != null) {
                    comparatorResult = 1;
                }
                else if (valueB != null) {
                    comparatorResult = -1;
                }
                return comparatorResult * (direction === 'asc' ? 1 : -1);
            });
        };
        /**
         * This method can be called by two filter types: string or an Object which extends TableFilter.
         *
         *  # String variant
         * Checks if a data object matches the data source's filter string. By default, each data object
         * is converted to a string of its properties and returns true if the filter has
         * at least one occurrence in that string.
         *
         * # TableFilter variant
         * Checks if a data object matches the data source's filter object. If several columns are defined,
         * the and-operator is applied. If a column filter is a list, the or-operator inside the list is applied.
         * The '_' property of the TableFilter can be used to search globally in all columns
         * (like the string variant above).
         *
         * By default, the filter string has its whitespace trimmed and the match is case-insensitive.
         * May be overridden for a custom implementation of filter matching.
         * @param data Data object used to check against the filter.
         * @param filter Filter string or Object which extends TableFilter that has been set on the data source.
         * @returns Whether the filter matches against the data
         */
        this.filterPredicate = (data, filter) => {
            const tableData = data;
            if (typeof filter === 'string') {
                return this._filterGlobally([filter.trim()], tableData);
            }
            const { _: globalFilter, ...propertyFilters } = this._normalizeTableFilter(filter);
            return (this._filterGlobally(globalFilter, tableData) &&
                this._filterProperties(propertyFilters, tableData));
        };
        this._data = new BehaviorSubject(initialData);
        this._updateChangeSubscription();
    }
    /**
     * Subscribe to changes that should trigger an update to the table's rendered rows. When the
     * changes occur, process the current state of the filter, sort, and pagination along with
     * the provided base data and send it to the table for rendering.
     */
    _updateChangeSubscription() {
        // Sorting and/or pagination should be watched if SbbSort and/or SbbPaginator are provided.
        // The events should emit whenever the component emits a change or initializes, or if no
        // component is provided, a stream with just a null event should be provided.
        // The `sortChange` and `pageChange` acts as a signal to the combineLatests below so that the
        // pipeline can progress to the next step. Note that the value from these streams are not used,
        // they purely act as a signal to progress in the pipeline.
        const sortChange = this._sort
            ? merge(this._sort.sortChange, this._sort.initialized)
            : of(null);
        const pageChange = this._paginator
            ? merge(this._paginator.page, this._internalPageChanges, this._paginator.initialized)
            : of(null);
        const dataStream = this._data;
        // Watch for base data or filter changes to provide a filtered set of data.
        const filteredData = combineLatest([dataStream, this._filter]).pipe(map(([data]) => this._filterData(data)));
        // Watch for filtered data or sort changes to provide an ordered set of data.
        const orderedData = combineLatest([filteredData, sortChange]).pipe(map(([data]) => this._orderData(data)));
        // Watch for ordered data or page changes to provide a paged set of data.
        const paginatedData = combineLatest([orderedData, pageChange]).pipe(map(([data]) => this._pageData(data)));
        // Watched for paged data changes and send the result to the table to render.
        this._renderChangesSubscription?.unsubscribe();
        this._renderChangesSubscription = paginatedData.subscribe((data) => this._renderData.next(data));
    }
    /**
     * Returns a filtered data array where each filter object contains the filter string within
     * the result of the filterTermAccessor function. If no filter is set, returns the data array
     * as provided.
     */
    _filterData(data) {
        // If there is a filter string, filter out data that does not contain it.
        // Each data object is converted to a string using the function defined by filterTermAccessor.
        // May be overridden for customization.
        this.filteredData =
            this.filter == null || this.filter === ''
                ? data
                : data.filter((obj) => this.filterPredicate(obj, this.filter));
        if (this.paginator) {
            this._updatePaginator(this.filteredData.length);
        }
        return this.filteredData;
    }
    /**
     * Returns a sorted copy of the data if SbbSort has a sort applied, otherwise just returns the
     * data array as provided. Uses the default data accessor for data lookup, unless a
     * sortDataAccessor function is defined.
     */
    _orderData(data) {
        // If there is no active sort or direction, return the data without trying to sort.
        if (!this.sort) {
            return data;
        }
        return this.sortData(data.slice(), this.sort);
    }
    /**
     * Returns a paged slice of the provided data array according to the provided SbbPaginator's page
     * index and length. If there is no paginator provided, returns the data array as provided.
     */
    _pageData(data) {
        if (!this.paginator) {
            return data;
        }
        const startIndex = this.paginator.pageIndex * this.paginator.pageSize;
        return data.slice(startIndex, startIndex + this.paginator.pageSize);
    }
    /**
     * Updates the paginator to reflect the length of the filtered data, and makes sure that the page
     * index does not exceed the paginator's last page. Values are changed in a resolved promise to
     * guard against making property changes within a round of change detection.
     */
    _updatePaginator(filteredDataLength) {
        Promise.resolve().then(() => {
            const paginator = this.paginator;
            if (!paginator) {
                return;
            }
            paginator.length = filteredDataLength;
            // If the page index is set beyond the page, reduce it to the last page.
            if (paginator.pageIndex > 0) {
                const lastPageIndex = Math.ceil(paginator.length / paginator.pageSize) - 1 || 0;
                const newPageIndex = Math.min(paginator.pageIndex, lastPageIndex);
                if (newPageIndex !== paginator.pageIndex) {
                    paginator.pageIndex = newPageIndex;
                    // Since the paginator only emits after user-generated changes,
                    // we need our own stream so we know to should re-render the data.
                    this._internalPageChanges.next();
                }
            }
        });
    }
    /**
     * Used by the SbbTable. Called when it connects to the data source.
     * @docs-private
     */
    connect() {
        if (!this._renderChangesSubscription) {
            this._updateChangeSubscription();
        }
        return this._renderData;
    }
    /**
     * Used by the SbbTable. Called when it disconnects from the data source.
     * @docs-private
     */
    disconnect() {
        this._renderChangesSubscription?.unsubscribe();
        this._renderChangesSubscription = null;
    }
    /** Converts a TableFilter object to a key value object of strings. */
    _normalizeTableFilter(tableFilter) {
        const normalizedTableFilter = { _: [] };
        Object.keys(tableFilter).forEach((key) => {
            if (typeof tableFilter[key] === 'undefined' ||
                `${tableFilter[key]}`.trim() === '' ||
                tableFilter[key] === null) {
                return;
            }
            if (typeof tableFilter[key] === 'string' || typeof tableFilter[key] === 'number') {
                normalizedTableFilter[key] = [`${tableFilter[key]}`.trim()];
                return;
            }
            const entry = tableFilter[key];
            if (Array.isArray(entry) && entry.length > 0) {
                normalizedTableFilter[key] = entry.map((value) => `${value}`.trim());
            }
        });
        return normalizedTableFilter;
    }
    /** Filters properties against tableData and returns true if matching data was found. */
    _filterProperties(propertyFilters, tableData) {
        return Object.keys(propertyFilters).every((key) => propertyFilters[key].some((value) => typeof tableData[key] !== 'undefined' &&
            tableData[key] !== null &&
            this._matchesStringCaseInsensitive(`${tableData[key]}`, value)));
    }
    /** Filters a list of strings against tableData and returns true if matching data was found. */
    _filterGlobally(filters, tableData) {
        return (filters.length === 0 ||
            filters.some((value) => this._matchesStringCaseInsensitive(this._reduceObjectToString(tableData), value)));
    }
    /** Checks if search string is in data (case insensitive). */
    _matchesStringCaseInsensitive(data, search) {
        return data.toUpperCase().indexOf(search.toUpperCase()) !== -1;
    }
    /** Reduces an object to a string. */
    _reduceObjectToString(data) {
        return Object.keys(data).reduce((currentTerm, key) => {
            // Use an obscure Unicode character to delimit the words in the concatenated string.
            // This avoids matches where the values of two columns combined will match the user's query
            // (e.g. `Flute` and `Stop` will match `Test`). The character is intended to be something
            // that has a very low chance of being typed in by somebody in a text field. This one in
            // particular is "White up-pointing triangle with dot" from
            // https://en.wikipedia.org/wiki/List_of_Unicode_characters
            return currentTerm + data[key] + '◬';
        }, '');
    }
}
/**
 * Data source that accepts a client-side data array and includes native support of filtering,
 * sorting (using Sort), and pagination (using Paginator).
 *
 * Allows for sort customization by overriding sortingDataAccessor, which defines how data
 * properties are accessed. Also allows for filter customization by overriding filterTermAccessor,
 * which defines how row data is converted to a string for filter matching.
 */
class SbbTableDataSource extends _SbbTableDataSource {
}

/**
 * Generated bundle index. Do not edit.
 */

export { SBB_SORT_DEFAULT_OPTIONS, SbbCell, SbbCellDef, SbbColumnDef, SbbFooterCell, SbbFooterCellDef, SbbFooterRow, SbbFooterRowDef, SbbHeaderCell, SbbHeaderCellDef, SbbHeaderRow, SbbHeaderRowDef, SbbRecycleRows, SbbRow, SbbRowDef, SbbSort, SbbSortHeader, SbbTable, SbbTableDataSource, SbbTableModule, SbbTableWrapper, SbbTextColumn, _SbbTableDataSource, sbbSortAnimations };
